import { useCallback, useState } from "react";
import { AUDIENCE_LABELS, CONTENT_TYPE_LABELS, TOPIC_LABELS } from "../../shared/model.ts";
import { FilterBar, type BrowseView } from "../components/FilterBar.tsx";
import { GridView } from "../components/GridView.tsx";
import { btn } from "../components/ui.tsx";
import { useStories } from "../hooks/useStories.ts";
import { DATE_RANGE_LABELS, DEFAULT_FILTERS, type Filters } from "../lib/filters.ts";
import { useColorBy } from "../lib/colorKeys.ts";
import { useApp } from "../state/AppState.tsx";
import { FEATURES } from "../lib/features.ts";
import { ROLE_LABELS, ROLE_LENS } from "../../shared/roles.ts";


export function DiscoverPage() {
  const app = useApp();
  // With the audience filter parked, ignore any saved audience so nothing is filtered invisibly.
  const [filters, setFilters] = useState<Filters>(() => ({ ...DEFAULT_FILTERS, audience: FEATURES.audienceFilter ? app.prefs.audience : "everyone", role: app.prefs.role }));
  // Browse is blocks only (the carousel is kept for the parked Briefing).
  const view: BrowseView = "grid";
  const onLoaded = useCallback((s: Parameters<typeof app.rememberStories>[0]) => app.rememberStories(s), [app.rememberStories]);
  // Search lives in one place: the ⌘K palette (the search button below opens it).
  const browse = useStories(app.repo, filters, onLoaded);
  const [colorBy, setColorBy] = useColorBy();
  const { visible, loaded, serverFilterKind, hasMore, status, error, loadMore, retry } = browse;

  const onChange = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    if (patch.audience) app.setPrefs({ audience: patch.audience });
    // A role also sets the summary style it reads (business impact, technical detail, plain English).
    if (patch.role) app.setPrefs({ role: patch.role, lens: ROLE_LENS[patch.role] });
  };
  const clear = () => {
    setFilters({ ...DEFAULT_FILTERS });
    app.setPrefs({ role: "all", lens: ROLE_LENS.all });
  };
  const refinedLocally =
    (filters.contentTypes.length > 0 || (serverFilterKind === "topic" && (filters.audience !== "everyone" || filters.role !== "all")));

  return (
    <div className="space-y-4 pb-24 pt-4 lg:pb-12">
      <div className="relative px-4 pt-2 sm:px-6 lg:px-8">
        <HeaderArt />
        <h1 className="relative font-display text-3xl font-bold leading-tight tracking-[-0.02em]">Databricks updates</h1>
        <p className="relative mt-1.5 max-w-2xl text-base text-ink-soft">Release notes, blog posts and press releases, newest first.</p>
      </div>

      <FilterBar
        filters={filters}
        onChange={onChange}
        colorBy={colorBy}
        onColorBy={setColorBy}
        view={view}
        summary={
          <p className="text-sm text-ink-soft" aria-live="polite">
            {status === "loading"
              ? "Loading updates…"
              : refinedLocally
                ? `Showing ${visible.length} matching of ${loaded.length} loaded${browse.total !== null ? ` (${browse.total.toLocaleString()} published)` : ""}`
                : `Showing ${visible.length} of ${browse.total?.toLocaleString() ?? "…"} updates`}
          </p>
        }
      />

      <ActiveFilters filters={filters} onChange={onChange} onClear={clear} />

      {status === "loading" ? (
        <div className="px-4 sm:px-6 lg:px-8" role="status">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-[13.5rem] animate-pulse rounded-xl border border-line bg-card" />
            ))}
          </div>
          <span className="sr-only">Loading updates…</span>
        </div>
      ) : status === "error" ? (
        <div role="alert" className="mx-4 rounded-xl border border-coral/40 bg-coral-wash p-6 sm:mx-6 lg:mx-8">
          <h2 className="font-semibold">Updates couldn't be loaded</h2>
          <p className="mt-1 text-sm text-ink">{error}</p>
          <button type="button" className={`${btn.secondary} mt-4`} onClick={() => void retry()}>
            Try again
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="mx-4 rounded-xl border border-dashed border-line-strong bg-card/70 p-8 text-center sm:mx-6 lg:mx-8">
          <h2 className="font-display text-xl font-semibold">No updates match these filters</h2>
          <p className="mx-auto mt-1 max-w-lg text-sm text-ink-soft">
            {filters.topic !== "all" && <>Topic: {TOPIC_LABELS[filters.topic]}. </>}
            {filters.role !== "all" && <>Role: {ROLE_LABELS[filters.role]}. </>}
            {filters.audience !== "everyone" && <>Audience: {AUDIENCE_LABELS[filters.audience]}. </>}
            {refinedLocally && hasMore
              ? "Some filters only check updates loaded so far; loading more may find matches."
              : "Try a broader topic, role, or date range."}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <button type="button" className={btn.primary} onClick={clear}>Clear filters</button>
            {refinedLocally && hasMore && (
              <button type="button" className={btn.secondary} onClick={() => void loadMore()} disabled={status !== "ready"}>
                Load more updates
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
        <h2 className="sr-only">Updates</h2>
        <GridView stories={visible} lens={app.prefs.lens} isSaved={app.isSaved} onToggleSave={app.toggleSave} hasMore={hasMore} loadingMore={status === "loading_more"} onLoadMore={() => void loadMore()} colorBy={colorBy} />
        </>
      )}

      {status === "ready" && refinedLocally && visible.length > 0 && (
        <p className="px-4 text-xs text-ink-soft sm:px-6 lg:px-8">
          Showing {visible.length} of {loaded.length} loaded updates. Type, search{serverFilterKind === "topic" && filters.role !== "all" ? ", and role" : ""}{serverFilterKind === "topic" && filters.audience !== "everyone" ? ", and audience" : ""} filters apply to loaded updates only.
        </p>
      )}
    </div>
  );
}

/** Filters hidden behind the Filters button, shown as removable chips once set (topic and role are visible already). */
function ActiveFilters({ filters, onChange, onClear }: { filters: Filters; onChange(p: Partial<Filters>): void; onClear(): void }) {
  const chips: { key: string; label: string; remove(): void }[] = [];
  if (FEATURES.audienceFilter && filters.audience !== "everyone")
    chips.push({ key: "aud", label: AUDIENCE_LABELS[filters.audience], remove: () => onChange({ audience: "everyone" }) });
  for (const t of filters.contentTypes)
    chips.push({ key: `t-${t}`, label: CONTENT_TYPE_LABELS[t], remove: () => onChange({ contentTypes: filters.contentTypes.filter((x) => x !== t) }) });
  if (filters.dateRange !== "any") chips.push({ key: "date", label: DATE_RANGE_LABELS[filters.dateRange], remove: () => onChange({ dateRange: "any" }) });
  const any = chips.length > 0 || filters.topic !== "all" || filters.role !== "all";
  if (!any) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 lg:px-8">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.remove}
          aria-label={`Remove filter: ${c.label}`}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-ink/25 bg-card px-3 text-sm font-medium text-ink hover:border-ink/60"
        >
          {c.label} <span aria-hidden="true" className="text-ink-soft">✕</span>
        </button>
      ))}
      <button type="button" onClick={onClear} className="min-h-9 rounded-lg px-2 text-sm font-semibold text-ink underline decoration-line-strong underline-offset-[3px] hover:decoration-ink">
        Clear all
      </button>
    </div>
  );
}

/** Decorative layered bands in the accent (Lava) tints, top right; hidden on phones. */
function HeaderArt() {
  return (
    <svg aria-hidden="true" viewBox="0 0 360 140" className="pointer-events-none absolute -top-4 right-0 hidden h-32 w-auto max-w-[45%] sm:block">
      <path d="M150 140 250 40l110 0v30L290 140Z" fill="#FF3621" opacity="0.08" />
      <path d="M200 140 280 60h80v40l-40 40Z" fill="#FF3621" opacity="0.16" />
      <path d="M255 140 315 80h45v60Z" fill="#FF3621" opacity="0.3" />
      <path d="M310 140 340 110h20v30Z" fill="#D92B1A" opacity="0.55" />
    </svg>
  );
}
